function finiteNumber(value, label) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    throw new TypeError(`${label} must be a finite number.`);
  }
  return numeric;
}

function optionalNumber(parameters, name, label) {
  const value = parameters.get(name);
  if (value === null || String(value).trim() === "") return 0;
  return finiteNumber(value, label);
}

/** Normalize a true-north direction to the half-open interval [0, 360). */
export function normalizeCardinalDegrees(value) {
  const degrees = finiteNumber(value, "Cardinal direction");
  return ((degrees % 360) + 360) % 360;
}

/** Return an earth-referenced direction relative to the line of fire. */
export function relativeCardinalDegrees(directionDegrees, bearingDegrees) {
  return normalizeCardinalDegrees(
    finiteNumber(directionDegrees, "Cardinal direction")
      - finiteNumber(bearingDegrees, "Target bearing"),
  );
}

function cleanComponent(value, speed) {
  const tolerance = Math.max(1, Math.abs(speed)) * 1e-12;
  return Math.abs(value) < tolerance ? 0 : value;
}

/** Convert wind-from direction and true-north bearing into engine components. */
export function cardinalWindComponents(speedMph, windFromDegrees,
  targetBearingDegrees) {
  const speed = finiteNumber(speedMph, "Wind speed");
  const relativeDegrees = relativeCardinalDegrees(
    windFromDegrees, targetBearingDegrees,
  );
  const relativeRadians = relativeDegrees * Math.PI / 180;
  return {
    wx: cleanComponent(-speed * Math.cos(relativeRadians), speed),
    wz: cleanComponent(-speed * Math.sin(relativeRadians), speed),
  };
}

/** Convert historical engine components back to a true-north wind-from input. */
export function cardinalWindFromComponents(rangeWindMph, crosswindMph,
  targetBearingDegrees) {
  const range = finiteNumber(rangeWindMph, "Range wind");
  const cross = finiteNumber(crosswindMph, "Crosswind");
  const bearing = normalizeCardinalDegrees(targetBearingDegrees);
  const windSpeedMph = Math.hypot(range, cross);
  if (windSpeedMph === 0) return { windSpeedMph: 0, windFromDegrees: 0 };
  const relativeDegrees = Math.atan2(-cross, -range) * 180 / Math.PI;
  return {
    windSpeedMph,
    windFromDegrees: normalizeCardinalDegrees(bearing + relativeDegrees),
  };
}

/**
 * Translate the cardinal wind controls shared by Trajectory and Range Table
 * into the historical trajectory-engine parameters. Blank wind values and a
 * blank firing azimuth are optional and therefore mean zero.
 */
export function prepareTrajectoryParameters(formData) {
  const parameters = new URLSearchParams(formData);
  if (parameters.has("wd") || parameters.has("ws")) {
    const windSpeedMph = optionalNumber(parameters, "ws", "Wind speed");
    if (windSpeedMph < 0 || windSpeedMph > 100) {
      throw new RangeError("Wind speed must be between 0 and 100 mph.");
    }
    const windFromDegrees = normalizeCardinalDegrees(
      optionalNumber(parameters, "wd", "Wind direction"),
    );
    const firingAzimuthDegrees = normalizeCardinalDegrees(
      optionalNumber(parameters, "faz", "Firing azimuth"),
    );
    const { wx, wz } = cardinalWindComponents(
      windSpeedMph, windFromDegrees, firingAzimuthDegrees,
    );
    parameters.delete("wd");
    parameters.delete("ws");
    parameters.set("wx", String(wx));
    parameters.set("wz", String(wz));
    parameters.set("faz", String(firingAzimuthDegrees));
  }
  parameters.set("elc", "on");
  parameters.set("azc", "on");
  return parameters;
}
